<script lang="ts">
  /**
   * "When does the tank usually run low?" — a 24-hour dial.
   *
   * The question is about *time of day*, and a time-of-day answer plotted on a
   * left-to-right axis makes 23:00 and 00:00 the two ends of the screen when
   * they are an hour apart. A dial closes the loop, which is the whole reason
   * clocks are round.
   *
   * ## Why the spokes are a rate, not a count
   *
   * A sensor that reports more often at midday would show more low readings at
   * midday whatever the water was doing. Each spoke is therefore the *share* of
   * that hour's own readings that came in at or below the low mark, so an hour
   * with forty readings and an hour with four are directly comparable.
   *
   * Lengths are then normalised to the busiest hour: an answer where the peak
   * hour is low 12% of the time is still an answer, and drawn to an absolute
   * 0–100% scale it would be twenty-four invisible stubs. The real percentages
   * are printed, so the normalisation cannot mislead.
   *
   * Inline SVG for the geometry, as in `TrendLine` — and, as there, every actual
   * glyph on the screen still comes from `Icon`. The hour labels are HTML rather
   * than SVG text: the dial scales with its container and SVG text would scale
   * with it, straight through the 11.5px floor.
   */
  import { fig, type ClockAnswer } from "../answers";

  let { a, tone = "ok" }: { a: ClockAnswer; tone?: "ok" | "watch" | "thin" } = $props();

  const C = 100;
  const INNER = 44;
  const OUTER = 90;

  /** Hour 0 at the top, clockwise — a 24-hour dial, read like a 12-hour one. */
  function point(hour: number, r: number): { x: number; y: number } {
    const rad = ((hour / 24) * 360 - 90) * (Math.PI / 180);
    return { x: C + r * Math.cos(rad), y: C + r * Math.sin(rad) };
  }

  const rates = $derived(
    a.hours.map((h) => ({ ...h, rate: h.readings > 0 ? h.low / h.readings : null })),
  );
  const peakRate = $derived(Math.max(...rates.map((h) => h.rate ?? 0), 0));
  const peakHour = $derived(
    peakRate > 0 ? (rates.find((h) => (h.rate ?? 0) === peakRate)?.hour ?? null) : null,
  );
  const silent = $derived(rates.filter((h) => h.readings === 0).length);

  const pad2 = (n: number) => String(n).padStart(2, "0");
  const spokeEnd = (rate: number) => INNER + (rate / (peakRate || 1)) * (OUTER - INNER);
</script>

<figure class="c" data-tone={tone}>
  <div class="c__dial">
    <svg
      viewBox="0 0 200 200"
      role="img"
      aria-label={peakHour === null
        ? "No hour has a low reading in this window."
        : `Lowest most often around ${pad2(peakHour)}:00, in ${Math.round(peakRate * 100)}% of that hour's readings.`}
    >
      <!-- The faint grid: the ring every spoke starts from, and a quarter mark
           at each of the four labelled hours. -->
      <circle cx={C} cy={C} r={INNER} class="c__ring" />
      <circle cx={C} cy={C} r={OUTER} class="c__ring c__ring--outer" />
      {#each [0, 6, 12, 18] as q (q)}
        {@const from = point(q, INNER)}
        {@const to = point(q, OUTER)}
        <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} class="c__quarter" />
      {/each}

      {#each rates as h (h.hour)}
        {#if h.rate === null}
          <!-- Reported nothing at all in this hour. Drawn as an absence on the
               ring rather than as a zero, which would claim the tank was never
               low when in fact nobody looked. -->
          {@const p = point(h.hour, INNER)}
          <circle cx={p.x} cy={p.y} r="1.4" class="c__silent" />
        {:else}
          {@const base = point(h.hour, INNER)}
          {@const tip = point(h.hour, spokeEnd(h.rate))}
          <circle cx={base.x} cy={base.y} r="1.6" class="c__base" />
          {#if h.rate > 0}
            <line
              x1={base.x}
              y1={base.y}
              x2={tip.x}
              y2={tip.y}
              class="c__spoke"
              class:c__spoke--peak={h.hour === peakHour}
            />
          {/if}
        {/if}
      {/each}
    </svg>

    <!-- HTML, not SVG text: these must hold 11.5px whatever size the dial is. -->
    <span class="c__h c__h--0">00</span>
    <span class="c__h c__h--6">06</span>
    <span class="c__h c__h--12">12</span>
    <span class="c__h c__h--18">18</span>

    <div class="c__centre">
      {#if peakHour === null}
        <span class="c__peak">—</span>
        <span class="c__lb">no low hour</span>
      {:else}
        <span class="c__peak">{pad2(peakHour)}:00</span>
        <span class="c__lb">{Math.round(peakRate * 100)}% of readings</span>
      {/if}
    </div>
  </div>

  <figcaption class="c__legend">
    <p class="c__note">
      Each spoke is one hour. Its length is the share of that hour's own readings that came in at or
      below {fig(a.lowMark)}{a.unit}, the level this tank sits under a fifth of the
      time. Lengths are relative to the longest spoke, not to 100%.
    </p>
    <ul class="c__keys">
      <li><span class="c__swatch c__swatch--peak"></span>lowest most often</li>
      <li><span class="c__swatch c__swatch--spoke"></span>every other hour</li>
      <li><span class="c__swatch c__swatch--dot"></span>reported, never low</li>
      {#if silent > 0}
        <li>
          <span class="c__swatch c__swatch--silent"></span>{silent}
          {silent === 1 ? "hour" : "hours"} with no reading at all
        </li>
      {/if}
    </ul>
  </figcaption>
</figure>

<style>
  .c {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-5);
    align-items: flex-start;
    /* Resolved on the figure root so the spoke, the centre figure and the legend
       swatch all read the same variable — three places that must never disagree
       about which hour is the peak. */
    --tone-c: var(--ok);
  }
  .c[data-tone="watch"] {
    --tone-c: var(--warn);
  }
  .c[data-tone="thin"] {
    --tone-c: var(--mut);
  }

  /* Padded so the four hour labels sit outside the outer ring rather than on it.
     A square box keeps the dial a circle at any container width. */
  .c__dial {
    position: relative;
    width: 250px;
    max-width: 100%;
    aspect-ratio: 1;
    padding: 17px;
    flex-shrink: 0;
  }
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  .c__ring {
    fill: none;
    stroke: var(--line);
    stroke-width: 1;
  }
  .c__ring--outer {
    stroke-dasharray: 2 5;
  }
  .c__quarter {
    stroke: var(--line);
    stroke-width: 1;
  }
  .c__spoke {
    stroke: var(--mut);
    stroke-width: 5;
    stroke-linecap: round;
  }
  /* The peak is distinguished four ways — length, width, colour and the hour
     printed in the middle of the dial. Colour is the least of them. */
  .c__spoke--peak {
    stroke: var(--tone-c);
    stroke-width: 8;
  }
  .c__base {
    fill: var(--mut);
  }
  .c__silent {
    fill: none;
    stroke: var(--line);
    stroke-width: 1;
  }

  .c__h {
    position: absolute;
    font-size: var(--fs-micro);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: var(--mut);
  }
  .c__h--0 {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  .c__h--12 {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  .c__h--6 {
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  .c__h--18 {
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  .c__centre {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    pointer-events: none;
    text-align: center;
  }
  .c__peak {
    font-family: var(--font-figure);
    font-variant-numeric: var(--figure-numeric);
    font-size: var(--fs-h3);
    font-weight: 700;
    letter-spacing: var(--figure-tracking);
    color: var(--tone-c);
  }
  .c__lb {
    font-size: var(--fs-micro);
    color: var(--mut);
    max-width: 11ch;
    line-height: 1.3;
  }

  .c__legend {
    flex: 1;
    min-width: 210px;
  }
  .c__note {
    margin: 0 0 var(--sp-3);
    font-size: var(--fs-small);
    color: var(--tx2);
    max-width: 46ch;
    line-height: 1.5;
  }
  .c__keys {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .c__keys li {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .c__swatch {
    width: 16px;
    height: 5px;
    border-radius: var(--r-pill);
    flex-shrink: 0;
  }
  .c__swatch--peak {
    background: var(--tone-c);
    height: 8px;
  }
  .c__swatch--spoke {
    background: var(--mut);
  }
  .c__swatch--dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--mut);
    margin: 0 5px;
  }
  .c__swatch--silent {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: none;
    box-shadow: inset 0 0 0 1px var(--line);
    margin: 0 5px;
  }
</style>
